import {Injectable} from '@angular/core';
import {ReplaySubject, Observable, Subject} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  private privateData = {
    id: null,
    trainigData: null,
    accountId: null,
    name: null,
    type: null,
    description: null,
    askAiUrl: null,
    displayName: null,
    category: null,
    scope: null,
    startDate: null,
    endDate: null,
    goal: null,
    status: null,
    nbLine: null,
    nbField: null,
    uploadedAt: null,
    fields: null,
    modelId: null,
    insightId: null,
    accuracyAvailable: null,
    accuracyPercentage: null,
    modelType: null,
    vendorDownloadedDatasetId: null,
    vendorUploadedModelId: null,
    vendorModelIdAccuracyDetails: null,
    autoModelIdAccuracyDetails: null,
    vendorModelIdStatus: null,
    downloadMetadata: false,
    resetPassword: false,
    mlProblem: null,
    image: null,
    haveTerms: 'no',
    withJupyter: 'no',
    jupyterHubUrl: null,
    prize: null,
    prizeDescription: null,
    video: null,
    resultLink: null,
    vendorStats: null,
  };
  public dataSubject: Subject<any> = new Subject<any>();
  public dataStream: Observable<any> = this.dataSubject.asObservable();

  get data() {
    return this.privateData;
  }

  set data(value: any) {
    Object.assign(this.privateData, value);
    this.dataSubject.next(value);
  }

  fileUploadProgressSubject = new ReplaySubject();
  enterpriseFileUploadSubject = new ReplaySubject();
  enterpriseDatasetUploadQueue: {
    value: number, fileName: string, size: number, id: number,
    uploadRequest: any, uploaded: number
  }[] = [];
  vendorModelUploadQueue: Mlmodel[] = [];
  enterpriseModelGenerateSubject = new ReplaySubject();
  enterpriseModelGenerateQueue: { datasetId: number, status: string, error?: string }[] = [];

  datasetFieldType = {
    default: '-',
    date: 'Date',
    categorical: 'Categorical',
    numerical: 'Numerical',
    text: 'General Text',
    id: 'Id',
    boolean: 'Boolean',
  };

  datasetCategory = {
    education: 'For Education',
    // 'challenge-prize' : 'Challenge',
    'challenge-no-prize': 'Challenge',
  };

  datasetCategoryWithoutChallenge = {
    education: 'For Education',
  };

  datasetClassificationScore = {
    // roc_auc_score: 'roc auc score',
    precision_score: 'precision score',
    recall_score: 'recall score',
    f1_score: 'f1 score',
    accuracy_score: 'accuracy score',
    mean_squared_error: '1 mean squared error',
    // confusion_matrix: 'confusion matrix',
    balanced_accuracy_score: 'balanced accuracy score',
  };

  datasetMultiLabelsClassificationScore = {
    precision_score: 'precision score',
    precision_score_micro: 'precision score micro',
    recall_score: 'recall score',
    recall_score_micro: 'recall score micro',
    f1_score: 'f1 score',
    f1_score_micro: 'f1 score micro',
    accuracy_score: 'accuracy score',
    hamming_loss_score: 'hamming loss score',
  };

  datasetRegressionScore = {
    r2_score: 'r2 score',
    mean_absolute_error: 'mean absolute error',
    median_absolute_error: 'median absolute error',
    mean_squared_error: 'mean squared error',
  };

  public defaultDatasetIcon = 'iVBORw0KGgoAAAANSUhEUgAAACsAAAAmCAYAAABdw2IeAAAYWWlDQ1BJQ0MgUHJvZmlsZQAAWIWVeQc4le8b//' +
    'Oe857JsfeWTfYme+89Uzn2pmOrJCRRSUYKKSRSNK2EjJRkpZRKCqFUGjb5v0b9vr/v77r+/+v/XNfzvp9zP/dzj2fdz/0eADgTyGFhQSg6AI' +
    'JDIii2Rrp8zi6ufLhxgAa0gAQwACZ7hofpWFubA6T8ef93mX8BoI33M8kNWf/b/n8t9F7e4Z4AQNYI9vAK9wxG8B0A4GTPMEoEAFgVhC4QHR' +
    'G2gd0QzERBDERw2Ab23cLJG9hjC+dt8tjb6iH4GgB4ajKZ4gsATR1C54vy9EXk0LxE2hhCvPxDENYZBGt6+pG9AODcifDsDA4O3cDOCBb1+Ic' +
    'c3/+S6fFXJpns+xdv+bJZ8Pr+4WFB5Nj/z+H4f5fgoMg/OoSRSu1HMbbd8BkZt5eBoWYbmBrBMyEellYIZkDwor/XJj+CUUS/SGOHLX4Ul2e' +
    '4HjJmgAXBMl5kfTMEcyHYMCTI0nyb7uHjb2iCYGSFoGL8I0zst/umeocb2G3LLKCE2lr9wT4UPZ3tvlVkyqbeDf72yEAHnW35L/28Tf7I/xX' +
    'nZ++0ZTOaGOXvaIlgGgSzhAfamW3xoAXj/PQs//BQIm037BdEsJp3iJHulnz0Xh+Koe02PyU4/I+/6FQ/fxPLbXw+ws/eeFvONU/ypv1sCK7' +
    'zDtFx+CPHO9zZ/I8vXt76Blu+o/u8Qxy2/UWPhkXo2m73/REWZL3NDxO9g4w26DsQzBUeZbfdF9aMQBbklnzYMizC2n7LTtgjgGxqvWUPHAP' +
    'MgR7QB3wgEqkeIBQEAP+emdoZ5NdWiyEgAwrwBd5Acpvyp4fTZksI8rQDceALgrxB+N9+uput3iAKoa/9pW49JYHPZmvUZo9A8BHBwcAMBCG' +
    '/Izd7hfzV5gjGEYr//2j3RGwNQupG2//SdBCK+TYl8o9cPto/nFgDrD7WGGuIFYM5YE1YHTZHntpIlYNVYNU/1v6HH/MR04/5gHmOGcW82ue' +
    'fSPmXP3zAAowiGgy3ffb4p8+wMCJVEdaFNRD5iGyYBeYAkrACokkH1kJ0KyJUvW3LN7z/t+z/8uEfo77NR5AhoAisBG2C6L970ojTKP6VsjG' +
    'm/xyhLVs9/o6r3t+Wf+vX+8dIeyFvs39zolPRt9Gd6Afox+hGdC3gQzej69Dd6Psb+O8qGt9cRX+02W7aE4jI8f8ffeRtnRsjGS5TKTMts7r' +
    'VFuEdE7GxwfRCw2Ip/r5+EXw6yMnvzWcS4im1k09ORk4GgI04snVM/bTdjA8QS+9/aGQkTqjIAUDU/Q8tFDkbqnKQrXHuPzRhZO+yqwJwy9Y' +
    'zkhK1RYM3HhhARCIUE2AHPEAAiCL+yAEloA60gQEwBVbAHriAvcgo+yHrmQKiwUFwBKSAdHAa5IDzoAiUgHJwHdwCtaARPAAPwRPQB56D18j' +
    'qmQCfwSyYBysQBOEgEsQIsUO8kBAkAclBKpAmZACZQ7aQC+QO+UIhUCR0EEqC0qEz0HnoElQB3YTqoQfQY6gfegW9h6ahH9AyCo2iRjGhuFH' +
    'CKGmUCkoHZYayR+1B+aL2o+JQyahTqDxUMeoaqgb1APUE9Rw1ivqMmkMDNBWaBc2PlkSroPXQVmhXtA+ago5Hp6Fz0cXoKnQDMs/P0KPoGfQ' +
    'SjIUZYT5YElnBxrAD7Anvh+PhE/B5uByugdvhZ/B7eBb+jSFhuDASGDWMCcYZ44uJxqRgcjFlmLuYDmQ3TWDmsVgsC1YEq4zsRhdsAPYA9gS' +
    '2EFuNbcH2Y8ewczgcjh0ngdPAWeHIuAhcCu4c7hquGTeAm8At4qnwvHg5vCHeFR+CT8Tn4q/im/AD+En8CoGOIERQI1gRvAixhAxCKaGB0Eu' +
    'YIKwQ6YkiRA2iPTGAeISYR6widhDfEH9SUVHtoFKlsqHyp0qgyqO6QfWI6j3VEjUDtTi1HrUbdST1Keor1C3Ur6h/kkgkYZI2yZUUQTpFqiC' +
    '1kUZIizSMNFI0JjReNIdp8mlqaAZovtISaIVodWj30sbR5tLepu2lnaEj0AnT6dGR6eLp8unq6Ybo5ugZ6WXpreiD6U/QX6V/TD/FgGMQZjB' +
    'g8GJIZihhaGMYY0QzCjDqMXoyJjGWMnYwTjBhmUSYTJgCmNKZrjP1MM0yMzArMDsyxzDnM99nHmVBswizmLAEsWSw3GJ5wbLMys2qw+rNepy' +
    '1inWAdYGNk02bzZstja2a7TnbMjsfuwF7IHsmey37Ww6YQ5zDhiOa4wJHB8cMJxOnOqcnZxrnLc5hLhSXOJct1wGuEq5urjluHm4j7jDuc9x' +
    't3DM8LDzaPAE82TxNPNO8jLyavP682bzNvJ/4mPl0+IL48vja+Wb5ufiN+SP5L/H38K/sENnhsCNxR/WOtwJEARUBH4FsgVaBWUFeQQvBg4K' +
    'VgsNCBCEVIT+hs0KdQgvCIsJOwseEa4WnRNhETETiRCpF3oiSRLVE94sWiw6KYcVUxALFCsX6xFHiiuJ+4vnivRIoCSUJf4lCif6dmJ2qO0N' +
    '2Fu8ckqSW1JGMkqyUfC/FImUulShVK/VVWlDaVTpTulP6t4yiTJBMqcxrWQZZU9lE2QbZH3Licp5y+XKD8iR5Q/nD8nXy3xUkFLwVLii8VGR' +
    'UtFA8ptiquKakrERRqlKaVhZUdlcuUB5SYVKxVjmh8kgVo6qreli1UXVJTUktQu2W2jd1SfVA9avqU7tEdnnvKt01prFDg6xxSWNUk0/TXfO' +
    'i5qgWvxZZq1jrg7aAtpd2mfakjphOgM41na+6MroU3bu6C3pqeof0WvTR+kb6afo9BgwGDgbnDUYMdxj6GlYazhopGh0wajHGGJsZZxoPmXC' +
    'beJpUmMyaKpseMm03ozazMztv9sFc3Jxi3mCBsjC1yLJ4YylkGWJZawWsTKyyrN5ai1jvt75ng7Wxtsm3+Wgra3vQttOO0W6f3VW7eXtd+wz' +
    '71w6iDpEOrY60jm6OFY4LTvpOZ5xGnaWdDzk/ceFw8Xepc8W5OrqWuc7tNtids3vCTdEtxe3FHpE9MXse7+XYG7T3/j7afeR9t90x7k7uV91' +
    'XyVbkYvKch4lHgcesp57nWc/PXtpe2V7T3hreZ7wnfTR8zvhM+Wr4ZvlO+2n55frN+Ov5n/f/HmAcUBSwEGgVeCVwPcgpqDoYH+weXB/CEBI' +
    'Y0h7KExoT2h8mEZYSNrpfbX/O/lmKGaUsHArfE14XwYRc2LsjRSOPRr6P0ozKj1qMdoy+HUMfExLTHSseezx2Ms4w7vIB+IDngdaD/AePHHx' +
    '/SOfQpXgo3iO+9bDA4eTDEwlGCeVHiEcCjzxNlEk8k/grySmpIZk7OSF57KjR0coUmhRKytAx9WNFqXCqf2rPcfnj547/TvNK60qXSc9NXz3' +
    'heaLrpOzJvJPrp3xO9WQoZVw4jT0dcvpFplZm+Rn6M3FnxrIssmqy+bLTsn/l7Mt5nKuQW3SWeDby7GieeV7dOcFzp8+tnvc7/zxfN7+6gKv' +
    'geMFCoVfhwAXtC1VF3EXpRcsX/S++vGR0qaZYuDi3BFsSVfKx1LG087LK5YoyjrL0srUrIVdGy23L2yuUKyqucl3NqERVRlZOX3O71ndd/3p' +
    'dlWTVpWqW6vQb4EbkjU833W++uGV2q/W2yu2qO0J3Cu4y3k2rgWpia2Zr/WpH61zq+utN61sb1Bvu3pO6d6WRvzH/PvP9jCZiU3LTenNc81x' +
    'LWMvMA98HY637Wl+3ObcNttu093SYdTx6aPiwrVOns/mRxqPGx2qP67tUumqfKD2p6VbsvvtU8endHqWeml7l3ro+1b6G/l39TQNaAw+e6T9' +
    '7OGgy+OS55fP+Fw4vXg65DY2+9Ho59Sro1ffhqOGV1wlvMG/S3tK9zR3hGil+J/auelRp9P57/ffdH+w+vB7zHPs8Hj6+OpH8kfQxd5J3smJ' +
    'Kbqpx2nC679PuTxOfwz6vzKR8of9S8FX0651v2t+6Z51nJ75Tvq//OPGT/eeVXwq/Wues50bmg+dXFtIW2RfLl1SWOpedlidXoldxq3lrYms' +
    'Nv81+v1kPXl8PI1PIm1cBNFJRPj4A/LgCAMkFAMY+5E6xeyvP2y5o5PKBQt6OkBT0GdWOToLtMNpYERwHno3AS9SgsqQOJJ2mqaedoZdk8GY' +
    'sYRpjEWeNZWvmoOV04irl/sm7iy+Z/6kAvaCt0EnhJ6JATF7cR+Lszi7JBWlRGRvZBLlK+eeKKCVZ5T0qaao1au93kTRUNN21jmvf1Hmjh9d' +
    'XMvA0PG1UZzxiCpkJmhtZBFhmWN2xfmmzaMdiL+9g5RjsdNK5yuWJ6/vds24Le1b2AXcimd1D0lPHy9Z7n4+3L9nPzn9XAF8gFDga1Bx8MSQ' +
    'p1C/Mer8KhS8cH/4t4kVkU1R5dFZMfGxQnMsBk4Mah5TjlQ6rJugcMUt0SvJOjjh6NCX7WGnq7eMtad3pL068Ozl56kvGj9NzmfNn5rLmspd' +
    'z4bPMeTvPGZ33zD9ckFdYdaG56MnFwUvDxaMl06W/ytBXmMvFK3SvulVGX8u+fquqv/r7Tfpb8rft7oTfPV1TUdtQ96C+raHl3r3Gu/ermyq' +
    'aS1oKH+S0prUdbA/osHuo1MnWufRo9HFv18Mnbd0Pnjb2VPfm9YX36w2QBp49yx/0ea74AvNiaKj8ZdQr7WHscCeyvhTfTL7NHFEfGXt3clR' +
    '99PP7og+2Y+ix6nGH8aWJ7I87PzZP2k6OTx2dlp4e/1T+OWRGfmbuS/VXz2/03+7OWs9+/H7wB+uPhz8zfoXMked9kHU0vtyxJrW+vjn/AtA' +
    'NVABaDj0F38QkYJ1xGnhJgghRhGoHtQxJjcaG1pMunr6IoYlxmpmORYWVzJbKfodjhIuKW55nN28C3yX+5h2vBeaEqIR5RRRFTcTcxWMlsnb' +
    'elOyWmpKBZfnldsm7KkQopiuVKterPFX9oPZrF1aDU1NWy0I7SCdD94Zen/4XQ7wRt7GciYGpg5mneYhFjGW8VZL1UZsU21S7NPsTDmmOyU6' +
    'xzn4u9q76u7XcDPe47o3el+N+g9zq0eXZ4XXXu8DngK+Tn4w/tf9MQF9gQ1BFcH5IRmhiGGW/G0U7nDd8JeJ55PWolGiPGINYmTjBA9wH2Q8' +
    'xx9Mdxh6eT/hwpCvxZlJOcvTRPSmmx/RTzY+T046kXz7x8OTIqa8Zc6cXMufO/Myazf6SM5P79eziObrzqvkhBWWFPRfGiqYvTlx6V/yqpL/' +
    '00eWmssYrXeVfrvJX7rlWcP1VNdMNy5upyOm1dFeqxqs2v26gAXNPoXHf/aNNZc2NLU0PrraebjvUHt2R8DCjs/BRyeMLXaeeRHbbPZXsgXu' +
    'Ge2/1pfcHDNg8Mxg0eG7zwmMo8mXyq2PDh177vNF7y/F2ZqT+3bFR5/eSH/AfPo61jRdO7P+oPUk9OThVMn34k/9nrxm/L8Ffw76FzYZ9p/y' +
    'I+hn7K3rOf95ogXbh9qLB4pMl16Uvy32r1GvDm/MvAdohM+glyhuNRWfAEnAvJg4rjZ3GXcb7EaQJS8QuqiLqaJItjRwtDe083Sv6FoYKxiy' +
    'mQ8y+LLasGmxi7MzsqxxTnANcTdxVPCW8+Xy5/Nk7MgRSBKOEyMIGInwii6LdYkXi4RLGO/klUZLTUkPSj2QaZK/K5cknKLgrqiphlXqVc1S' +
    'cVdlVX6kVqnvtktPAaoxo1mhlaPvp6OsK69HpA/2fBpOGL4zuGeeaeJsKmY6a5ZlbWeAs2iyTrEys2aw/2TTZZtn52as7kBxGHK87HXQ2dWF' +
    '2eedavjsUif9Le+7vTdin54537ycXeAR67vKi9hr2vuKz31fFd9Wv2T8hQDsQBLYEHQnWC4FDOkKPhumELe6vpLggMbsiwiriV2Re1K6okei' +
    'EGO6Y+7HucSxxwwcqDyYdco4XjZ8/3JaQdcQ3UT9JPJntKFUKSPl1bCz16fHqtBPp5BMKJ3Enh0/dyEg7HZhpdIbhzMOs3Vkz2XE5Orm6Z1P' +
    'P4c+n5Y8Xsl+QK1K9qHpJsVi6RLSU/zJ7Gf0VYjmhghZZSRrX3K8fq7pe/ezG6i3R2653ztztr2Wqc6kvaBhqxNwXazJq9mg5/OBCa1Pbu/b' +
    '1h/ydeo98H5/ouvnkRfdaj1jv7r6z/SPP5AZPPv86ZPeyfpj/dc5b6Xc076PH06div1j+mF+y2Zj/re99GwWrBEAWkmc6nkTqNACZtUieeQ8' +
    'AViIA1iQA7FUB6lgVQBlVASjw6N/4ASGJJx7JOVkALxADCkimaQ5ckaw5BhxHMsproAkMgI9gFWKAxCBtJD8Mh04i+WAHNIaCUPwoXZQX6hi' +
    'S5Q2gltECaAt0HLocPQTjYTU4GC6BX2EYMGZIRtaGhbDa2ARsKw6DM8Wdxr3E8+OD8PUEHMGJUE5YJloQLxEXqCypyqlhag/qNpIQ6TjpK40' +
    '9TSOS6WTSAbr9dOP0LvS9DIYM9xlVGGuY1JjamG2Zx1giWbGsuWzCbHXsluxTHKmcspxjXEXcHjwSPIu8D/ly+L12KAhgBV4L3hbKEA4SMRO' +
    'VECOJzYo/l7i384JkvJSbtKoMk8ys7FO5q/LHFfwUTZWklJmV11W+qI6oDah37erQaNfs1OrRHtaZ0p3XBwZY5JzDG+NNCKbUZkzm/BYKlpZ' +
    'WIdbZNo22E/YkBwVHF6dDzhdd2l0n3aj2yOx13HfQvZTc47HoJeht53PUt9FvOUAv8FzQUohn6MB+Q0pjhEJkdbRkzM24XQf6DoUe5kp4kZi' +
    'dbH50/lj28Z1pHSe8TzFnvMt8mvU2Zz2P77xqgfmFfRdjiy+WDl+RrLh4TaZq9OalO3trqeqrGvc0S7Tydhg+Ku6m7hXtnx/MHBJ91f/mwru' +
    'zHwY+uk8vfWH4du0H+CUzr7qwvpS2XLcyuHpvreR32Lry5vkBbX5zYACcQBjIAS1gAdxAMIgHmaAU1INeMAHWIBZIGjKFfKAkqBh6AH1AwSg' +
    'RlDmKgjqPakN9Q3OhzdAH0dXocZgDtoXT4Q4MhNHAHMDcw6xitbBJ2Mc4OpwL7jLuB14Hn4X/SFAnZBFmiIbInK9SOVPdQTJhCvUgSZV0kYa' +
    'KJoZmktaFtofOkK6FXpO+mUGPoYvRjvEtkpkuM2ewiLM8Yd3PxsJWw27D/pEjlpPEWcqlzTXOncljykvD+5bvNv+pHf4CuoJsgp+F7gufFvE' +
    'R1RUTEmeQwO/ESOKlaKQZZOhl8bJLclPyQwpdig+UHih3qbxW/aFOs0tGw0bTXytCm6Ljp+usZ6SvaqBgqGJkZLzPJN70klmn+awlp5WBdSA' +
    'S07LtztrnOGQ7XnRqdv7uqrg7we3pXp59Ee69HgKePl453nd9enzH/VYCWALlg+yDo0LOh7aEfaKwhhtGREVeiRqOoYu1iMs48PKQcPyhw2N' +
    'HfJPokrtSIlKxx4+lwydST3FmtGUmZjnn6J1VP6eer16oWiR2CS5+WBpVxnnlfoVHJfO1t1UdN3pvzd2VrT1Y/6SRtkm/hdJa1j7dqfv4Vrd' +
    'sT0Hf24Ffg99fTL4cG5568+sd9J44xjQhOGk8nTuj/C3tZ9lC0FLPSvJq29qv30ub849Cdj894AGSQBPYAB9wCOSCG6AbfIIIkARkAVGgPKg' +
    'F+oRiQemjIlBlqGE0PdoEnYxuQa/B6nAc3ACvYnQwaZghrBj2CPYtThNXjMfjQ/GDBFVCIRFFDCA+p9KnuketSv2AZE36SJNIy0/bQudGN09' +
    '/mkGS4SljCBOJqZxZl/kNSywrD2sP2yl2Dw5dTnEuJq4V7rc8dbxn+IL5zXfICLAJYgWXhL4LfxP5KbomTiMhuFNb0l0qQbpQpk72mdxPBQ5' +
    'FE6VE5TZVajU39RsaOOSu2qSzQzdLn8WgysjVhN603/y8Zai1g62c3bCDq2O3s7HLs90+bot7k9whcpjHcy9l7wJfgt+RAGJgSbBFKAirpYR' +
    'G8ES2RUfGeh34Gl+aEHvkReJqMuooPoXumHxq+PHBdIcT06dST0tlvspKzVHP/Z5XcX5vAbHwSpHyxfvFWiUtl/XLusqtKwYr7a/1VRlW198' +
    'UvXX2Dv7uoZrVuuMNwvf67ic2K7VMtxa0Wz2EO+89Dn8i0T3ec6HPeYDp2cDzjCHTl+vD195YvZ16Fzm69iFxHD2ROImaSvoEfz488/Wr4bf' +
    'Y2cLvJ39E/tT/ufDr6pzl3Ot5v/n5haiF6UW3xd4lvaXKZdJy2PLAiuJK3sr3VZPV4tWVNfu167/Rv51/X1uH1h3Wr27Mf7iPvNxm+ICodQH' +
    'AjKyv/xQGAHcGgLXM9fWV4vX1tRIk2XgDQEvQ1n9Im7GGDoCCdxuoS/x5wr//y/k/+M/fTpQ5i/EAAAGbaVRYdFhNTDpjb20uYWRvYmUueG1' +
    'wAAAAAAA8eDp4bXBtZXRhIHhtbG5zOng9ImFkb2JlOm5zOm1ldGEvIiB4OnhtcHRrPSJYTVAgQ29yZSA1LjQuMCI+CiAgIDxyZGY6UkRGIHh' +
    'tbG5zOnJkZj0iaHR0cDovL3d3dy53My5vcmcvMTk5OS8wMi8yMi1yZGYtc3ludGF4LW5zIyI+CiAgICAgIDxyZGY6RGVzY3JpcHRpb24gcmR' +
    'mOmFib3V0PSIiCiAgICAgICAgICAgIHhtbG5zOmV4aWY9Imh0dHA6Ly9ucy5hZG9iZS5jb20vZXhpZi8xLjAvIj4KICAgICAgICAgPGV4aWY' +
    '6UGl4ZWxYRGltZW5zaW9uPjQzPC9leGlmOlBpeGVsWERpbWVuc2lvbj4KICAgICAgICAgPGV4aWY6UGl4ZWxZRGltZW5zaW9uPjM4PC9leGl' +
    'mOlBpeGVsWURpbWVuc2lvbj4KICAgICAgPC9yZGY6RGVzY3JpcHRpb24+CiAgIDwvcmRmOlJERj4KPC94OnhtcG1ldGE+CrK6v5gAAAS9SUR' +
    'BVFgJ1Vj7U1pHFD4gKBcF1PjgpTRWE6FJnaRqpu30f+20/0Ob9oekMx3bJiYxqU61icbMFApEfPFG0OR8e7Pk4sDdC5f84GHu7H3t2Y9z93z' +
    'n23W8Y6MrYs4rglPAdPUDbKPRoEqlSpVqhaoVPqo1crlc5NE8pHk00XqGhsjhcNgarmuwudwhZTIZSmczlM/nBUiAVRmADjFgr9dLU5OTFAo' +
    'FKRQM0uDgoKpr87nDypwFmFe7u7S9vUP5QqHZ2e7JwMAAzV2/TolEnMZGR5XulGALDO7Bw9/o+ORE6azXF5xOJ62uLNPizZumLpQJtvbHn58' +
    'UKNBdXFzQX48e0/HxsT2wmWzW1EE/H2ayb03dKSNr2rvvD80pXwm2m2y1i101lhKsatLbBSj7g9JmZ2bkZdtWyQbo9c/2Nm08f0H1er2tE7s' +
    '3w+EwffftN6RpmqkrS2DhAUBf7e7R/v4+5Q4Pya6kQCSjkbCgq/HxcVOQ8qES7Iu/NwVpu7l8SgPwLGfuKVewKpdYvdRW+ZwPlNtajUD4iJT' +
    'm8TTLrobyq3lpemqS/H6/dCfadDpD7/gXDoVa7hsvlGC//+FH8vCAXyQSFJud5UF8xv62zutcGdPptKiMaS7h91ZXKL642NHnx3B1fIVExJ4' +
    '+e0Y4/D4fRSIRCgT85EXkOFKaV48gotnOEOlKGV+gTGWOfLlcJoDD10FBsGpKsPiUFR5AGrRBfmdHXra0ECr6Z9eo0ajrAHlqWAWkSjAldS1' +
    '9ebsFkNlFjSN4cnoqVBnUWYkjaBUokmwmGjVzT8o5i97/JZO0/uQJS8L+KS6JClNnYX6eVpa/Ekkp77drLYGVHZPJFL1+sy8iV+Y52KsB4OT' +
    'EhJj7CwvzBGFuxZRgf19boztLSzQyMtLiD8Jbpy5eITRXCR9WCx9WCpi/oCsPrxYEbaHlZJy4dq0lipgq0MuY85/FYi3jGC+UCba395oLwRv' +
    '6fG6OYrFZoe4RGfDkZa40OrZyjvmNr/Xvy5cE3QzqMjMlWHSW/xz/HkCDwWkaDQQ4WoiYfug0ponoyAHPz88Fk4BNQFlocZRKZTGViqWSfNV' +
    'SqwQLXjUuZQAglfpfHO1GwJ9BEUGVOzs7a/dKx3sYy8yU1LVsIUuNA+DPlDhi3QIFbUHQmJkywdD56OiInm5sdIym2QCqZ8PDw6KUJ+Kdy6z' +
    '0YQmsfBnZj2RDqTw4OLBM+LK/bH2+EZGoKNszrGGdFvcTlGB/+vk+3b17h4LT03Is0eJzF4tF3tiA0gJlQXXpCgzqy+V2iQ2Oj6oL6ksT+wa' +
    '4ZzRoh83NLdYbAbrBvNvJlAn2liN4/5dfBVioLkQDyguJBOc4ejGpuJKplPhaSMi+UBfAYJUrVrrr60J5AWSTtqC6DBQGNsDGiJGuoLrkNTR' +
    'DLpfrehopIwuBgQQzmlBeTOL9NtWKQUldX99bFbKv38Au+0vE47yCmLp8u+VamWB4G6Jlc2uLdrmCYa710wDw9q1bFI1GlG4tgZVekASQi1g' +
    'vZXgXsVAoykeWW7fbLXYRg7yDiAXj2NiY5b5dgb3s9YI3zUFbkrL0/Vm+rjF1YX+2qbZ02gJlAWyvZgtsr4P22k+ZYL06/hT9rhTY9yYNU5i' +
    'cfTpmAAAAAElFTkSuQmCC';

  constructor() {
  }

  setData(obj) {
    Object.assign(this.data, obj);
  }

  downloadFile(data, header, filename = 'data') {
    const csvData = this.ConvertToCSV(data, header);
    const blob = new Blob(['\ufeff' + csvData], {type: 'text/csv;charset=utf-8;'});
    const dwldLink = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const isSafariBrowser = navigator.userAgent.indexOf('Safari') !== -1 &&
      navigator.userAgent.indexOf('Chrome') === -1;
    if (isSafariBrowser) {  // if Safari open in new window to save file with random filename.
      dwldLink.setAttribute('target', '_blank');
    }
    dwldLink.setAttribute('href', url);
    dwldLink.setAttribute('download', filename + '.csv');
    dwldLink.style.visibility = 'hidden';
    document.body.appendChild(dwldLink);
    dwldLink.click();
    document.body.removeChild(dwldLink);
  }

  ConvertToCSV(objArray, headerList) {
    const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
    let str = '';
    let row = '';

    headerList.forEach(header => {
      row += header + ',';
    });
    row = row.slice(0, -1); // stripping last comma

    str += row + '\r\n';

    array.forEach(entry => {
      row = '';
      entry.forEach(element => {
        row += ((typeof element === 'string' || element instanceof String) ? '"' + element.replace(/"/g, '') + '"' : element) + ',';
      });
      row = row.slice(0, -1); // stripping last comma
      str += row + '\r\n';
    });
    return str;
  }

  /**
   * Get LocalDate
   *
   * @param stringDate The date
   * @return localDate
   */
  public getLocalDateTimeStringFromIso8061(endDateIsoString: string): string {
    return new Date(endDateIsoString).toLocaleDateString() + ' ' + new Date(endDateIsoString).toLocaleTimeString();
  }

  public isEndDateExpired(endDateIsoString: string, delay = 0, defaultValue: boolean = false): any {
    if (endDateIsoString === null) {
      return defaultValue;
    }
    const endDate = new Date(endDateIsoString);
    if (delay > 0) {
      endDate.setDate(endDate.getDate() + delay);
    }
    return endDate.getTime() - new Date().getTime() < 0;
  }
}

export class Mlmodel {
  id: string;
  accuracy: number;
  type: string; // best or auto
  datasetId: string;
}

export enum DatasetBannerView {
  DatasetView = 'DatasetView',
  PredictionView = 'PredictionView',
  TeamView = 'TeamView',
  ModalListView = 'ModalListView',
  ForumView = 'ForumView',
  LeaderboardView = 'LeaderboardView',
  RulesView = 'RulesView',
  PrizeView = 'PrizeView',
  AIView = 'AIView',
}

@Injectable({
  providedIn: 'root'
})
export class DatasetBannerModel {
  datasetBannerUpdate = new ReplaySubject();
  datasetId: number;
  backgroundImage = '';
  isDatasetView = false;
  isModalView = false;
  isPredictionView = false;
  isTeamView = false;
  isForumView = false;
  isRulesView = false;
  isLeaderboardView = false;
  isPrizeView = false;
  isAskAIView = false;

  dataset = {
    id: null,
    image: null,
    goal: null,
    description: null,
    askAiUrl: null,
    mlProblem: null,
    targetColumn: null,
    startDate: null,
    endDate: null,
    nbLine: null,
    nbField: null,
    displayName: null,
    targetColumnNames: null,
    video: null,
    vendorStats: null,
  };

  public setBannerView(view: DatasetBannerView) {
    this.isDatasetView = false;
    this.isModalView = false;
    this.isPredictionView = false;
    this.isTeamView = false;
    this.isForumView = false;
    this.isLeaderboardView = false;
    this.isRulesView = false;
    this.isPrizeView = false;
    this.isAskAIView = false;

    switch (view) {
      case DatasetBannerView.DatasetView :
        this.isDatasetView = true;
        break;
      case DatasetBannerView.ModalListView:
        this.isModalView = true;
        break;
      case DatasetBannerView.PredictionView:
        this.isPredictionView = true;
        break;
      case DatasetBannerView.TeamView:
        this.isTeamView = true;
        break;
      case DatasetBannerView.ForumView:
        this.isForumView = true;
        break;
      case DatasetBannerView.LeaderboardView:
        this.isLeaderboardView = true;
        break;
      case DatasetBannerView.RulesView:
        this.isRulesView = true;
        break;
      case DatasetBannerView.PrizeView:
        this.isPrizeView = true;
        break;
      case DatasetBannerView.AIView:
        this.isAskAIView = true;
        break;
    }
    this.datasetBannerUpdate.next({});
  }
}
